// Pure rules of panel R after the re-point confirm: the points confirmed, activated,
// migrated, rolling back and closed as rolled back. jest cannot load .vue files, so
// every decision the panel makes lives here and the component only renders it. The
// server owns what is allowed (steps.reasons and the flags); this file says where the
// rollback is, what is left to do, in which order and what to tell the operator.

import {
  DEFAULT_WINDOW_DURATION,
  ROLLBACK_TYPE,
  batchSummary,
  driftKind,
  inScope,
  newestEvent,
  plural,
  requiresTypedName,
  statusOf,
  unremoved,
  windowFromForm,
  windowShortText,
  windowToForm,
} from '@/utils/cvcMigrationPage';
import {
  CUSTOMERS_UNTIL_REPOINT_BACK,
  action,
  rollbackActions,
  rollbackAnswerView,
  rollbackContext,
  rollbackNotPossible,
  rollbackPoint,
  rollbackTexts,
} from '@/utils/cvcMigrationRollback';
import { toDate } from '@/utils/zonedTime';

// The points that open the rollback after the confirm. Release A's points before the
// confirm are not here and are not changed.
export const AFTER_POINTS = ['confirmed', 'activated', 'migrated', 'rollingBack', 'closedRolledBack'];

const timeMs = (value) => {
  const date = toDate(value);
  return date ? date.getTime() : 0;
};
const by = (who) => (who ? ` by ${who}` : '');
const newest = (rows, key) => rows.reduce((best, r) => (!best || timeMs(r[key]) > timeMs(best[key]) ? r : best), null);
const eventTime = (event) => (event ? event.updatedAt || event.createdAt : null);

// ---- restore outcomes ----

// A restore that failed for a reason a retry does not change: the page lists these as
// "not restorable" and the operator fixes them in VISP. Anything else (RADIUS not
// reachable, the time budget, an exception) is retried and never counts as handled.
// The texts are the ones VispApi writes (CvcMigrationRestore and the restore route).
const PERMANENT_STARTS = [
  'Not restored: RADIUS now has ',
  'Not restored: no old IP on record',
  'Not restored: no netmask and gateway on record',
  'Not restored: service not found',
  'Not restored: subscriber not found in RADIUS',
];
const PERMANENT_PATTERNS = [
  /^Not restored: old IP \S+ is now used by service /,
  /^Not restored: old IP \S+ has status .* in the VISP pool table/,
];

export function isPermanentFailure (outcome) {
  const text = typeof outcome === 'string' ? outcome : '';
  return PERMANENT_STARTS.some(start => text.startsWith(start)) || PERMANENT_PATTERNS.some(pattern => pattern.test(text));
}

const isRestoreFailure = (outcome) => /^not restored/i.test(typeof outcome === 'string' ? outcome : '');
const stillOnNewIp = (s) => s.state === 'committed' || s.state === 'unknown';

// ---- small readers ----

// Statics whose RADIUS service has no row in the statics ledger (committed before the
// ledger existed): the tool cannot put their old IP back. An older server that sends no
// ledger block says nothing.
export function noLedgerStatics (preflight) {
  if (!preflight || !preflight.rollback) return 0;
  const known = (Array.isArray(preflight.rollback.statics) ? preflight.rollback.statics : []).map(s => s.radiusServiceId);
  return (preflight.rows || [])
    .filter(r => r.avcClass === 'VISP' && r.ipType === 'Static' && r.radiusServiceId !== null && r.radiusServiceId !== undefined)
    .filter(r => !known.includes(r.radiusServiceId))
    .length;
}

// The Optus window ended and the re-point back was not confirmed.
export function windowEnded (record, nowUtc) {
  const end = toDate(record && record.rollbackWindowEnd);
  const now = toDate(nowUtc);
  return !!end && !!now && now > end && !(record && record.repointBackConfirmedAt);
}

// Services a restored kick would drop: on the source with the new IP.
export function kickCandidates (reconnect) {
  const block = reconnect && reconnect.rollback;
  return block && Array.isArray(block.kickCandidates) ? block.kickCandidates : [];
}

const missingInterfaces = (answer) => [['CVC', answer && answer.cvcInterface], ['loop', answer && answer.loopInterface]]
  .filter(([, found]) => found && found.present === false)
  .map(([kind, found]) => ({ kind, name: found.interface }));

// ---- context ----

// Everything the texts, steps and alerts are made of, read once from the Preflight, the
// last reconnection answer and this session's last "Verify source" (`verify` is
// `{ answer, at }` or null).
export function afterContext (preflight, reconnect, verify, nowUtc) {
  const base = rollbackContext(preflight, reconnect, nowUtc);
  const record = base.record;
  const rows = (preflight && preflight.rows) || [];
  const point = rollbackPoint(preflight, reconnect);
  const evidence = (reconnect && reconnect.rollback) || null;
  const copies = unremoved(record.sourceCopies);
  const scopeIds = rows.filter(r => r.inRouterScope).map(r => r.avcId);
  const copyIds = copies.map(c => c.avcId);
  const inScopeCopies = copies.filter(c => scopeIds.includes(c.avcId));
  const activated = !!record.activatedAt;
  const answer = (verify && verify.answer) || null;
  const details = (answer && answer.details) || [];
  const missing = missingInterfaces(answer);
  const rbWindow = record.rollbackWindowStart && record.rollbackWindowEnd
    ? windowShortText(record.rollbackWindowStart, record.rollbackWindowEnd, base.poiState)
    : '';
  const startedMs = timeMs(record.rollbackStartedAt);
  const backEvent = newestEvent(record, [ROLLBACK_TYPE]);
  const pendingSource = (preflight && preflight.hostPendingEvents && preflight.hostPendingEvents.source) || 0;
  const driftCopies = copies.filter(c => !scopeIds.includes(c.avcId));
  const buildIds = activated ? rows.filter(r => inScope(r) && !copyIds.includes(r.avcId)).map(r => r.avcId) : [];
  const policyIds = details.filter(d => driftKind(d) === 'policy').map(d => d.avcId);
  return {
    ...base,
    point,
    activated,
    allRows: rows,
    localName: (preflight && preflight.localNetwork && preflight.localNetwork.customerName) || '',
    // the rollback window ("" when not booked)
    rbWindow,
    rbWindowEnded: windowEnded(record, nowUtc),
    // source copies: in router scope, shut among them, and those of AVCs that left or were suspended
    copiesInScope: inScopeCopies,
    copiesShut: inScopeCopies.filter(c => c.shutAt),
    driftCopies,
    // AVCs of an activated record that have no copy on the source, and AVCs whose speed policy differs
    buildIds,
    policyIds,
    b: buildIds.length,
    d: driftCopies.length,
    p: policyIds.length,
    pendingSource,
    // this session's last verify of the source
    verify: answer ? { at: verify.at, differ: details.filter(d => d.match === false).length + missing.length, missing } : null,
    // the newest bring up since the start
    bringUpEvent: backEvent && timeMs(backEvent.createdAt) >= startedMs ? backEvent : null,
    // statics: still on a new IP after a failed restore, and the failures a retry can fix
    noLedger: noLedgerStatics(preflight),
    restoreFailures: base.ledger.filter(s => stillOnNewIp(s) && isRestoreFailure(s.lastOutcome) && timeMs(s.lastOutcomeAt) >= startedMs),
    // the reconnection evidence of the rollback
    evidence,
    backOnSource: (evidence && evidence.backOnSourceCount) || 0,
    firstBackOnSource: evidence ? evidence.firstBackOnSourceUtc : null,
    droppedFromTarget: (evidence && evidence.droppedFromTargetCount) || 0,
    evidenceMonitored: (evidence && evidence.monitoredCount) || 0,
    evidenceSince: evidence ? evidence.sinceUtc : null,
    evidenceRows: (evidence && evidence.rows) || [],
    candidates: kickCandidates(reconnect),
  };
}

// ---- steps ----

const STATUS_OF_STATE = [[/^Done/, 'done'], [/^Failed/, 'failed'], [/^Running$/, 'running'], [/^Not needed$/, 'notNeeded']];
const TONE_OF_STATUS = { done: 'green', failed: 'red', running: 'amber', todo: '', notNeeded: '' };
const OPEN_STATUSES = ['todo', 'running', 'failed'];

const stepOf = (key, label, system, duration, state, status) => {
  const found = STATUS_OF_STATE.find(([pattern]) => pattern.test(state));
  const s = status || (found ? found[1] : 'todo');
  return { key, label, system, duration, state, status: s, tone: TONE_OF_STATUS[s] };
};
const cronSystem = (host) => `Tool: Usher job on ${host} (CVC cron, every 5 min)`;

function checkState (ctx) {
  const started = timeMs(ctx.record.rollbackStartedAt);
  if (ctx.verify && ctx.verify.differ > 0) return `Failed ${ctx.fmt(ctx.verify.at)}: ${ctx.verify.differ} differ`;
  const every = ctx.copiesInScope.length > 0 && ctx.copiesInScope.every(c => c.verifiedOkAt && timeMs(c.verifiedOkAt) >= started);
  return every ? 'Done' : 'To do';
}

function fixState (ctx) {
  if (ctx.b === 0 && ctx.d === 0 && (!ctx.verify || ctx.p === 0)) return 'Not needed';
  return ctx.pendingSource > 0 ? 'Running' : 'To do';
}

function bringUpState (ctx) {
  const event = ctx.bringUpEvent;
  if (!event) return ctx.copiesShut.length === 0 ? 'Not needed' : 'To do';
  const status = statusOf(event);
  if (status === 'pending') return 'Running';
  if (status === 'complete') return `Done ${ctx.fmt(eventTime(event))}${by(event.createdBy)}`;
  if (status === 'failed') return `Failed ${ctx.fmt(eventTime(event))}: ${event.error || 'no reason recorded'}`;
  return 'To do';
}

function restoreState (ctx) {
  if (ctx.restoreTotal === 0) return 'Not needed';
  if (ctx.c === 0) {
    const last = newest(ctx.ledger.filter(s => s.restoredAt), 'restoredAt');
    return last ? `Done ${ctx.fmt(last.restoredAt)}${by(last.restoredBy)}` : 'To do';
  }
  const failed = newest(ctx.restoreFailures, 'lastOutcomeAt');
  return failed ? `Failed ${ctx.fmt(failed.lastOutcomeAt)}: ${ctx.c} not restored` : 'To do';
}

function optusStep (ctx) {
  const label = `Optus re-points ${ctx.cvc} back to ${ctx.source}`;
  const duration = ctx.rbWindow || 'not booked yet';
  if (ctx.backOnSource > 0) {
    const since = ctx.fmt(ctx.firstBackOnSource || ctx.evidenceSince);
    return stepOf('optus', label, 'You: Optus', duration,
      `Back on ${ctx.source} since ${since}: ${ctx.backOnSource} of ${ctx.evidenceMonitored} services`, 'done');
  }
  if (ctx.record.repointBackConfirmedAt) {
    return stepOf('optus', label, 'You: Optus', duration, `Confirmed ${ctx.fmt(ctx.record.repointBackConfirmedAt)}`, 'done');
  }
  return stepOf('optus', label, 'You: Optus', duration, 'To do');
}

const doneBy = (ctx, at, who) => (at ? `Done ${ctx.fmt(at)}${by(who)}` : 'To do');

// The ten steps of a rollback, in the order they run, each with its system, its time and
// where it stands. Only a rollback in progress has steps.
export function afterSteps (ctx) {
  if (ctx.point !== 'rollingBack') return [];
  const record = ctx.record;
  const source = ctx.source;
  const total = ctx.c > 0 ? ctx.c : ctx.restoreTotal;
  const retry = ctx.restoreFailures.filter(s => !isPermanentFailure(s.lastOutcome)).length;
  const seconds = Math.max(2, Math.ceil(total / 2));
  const shutNow = ctx.copiesShut.length;
  const steps = [
    stepOf('start', 'Start the rollback', 'Tool: Usher data', 'instant', doneBy(ctx, record.rollbackStartedAt, record.rollbackStartedBy)),
    stepOf('check', `Check ${source}`, `Tool: router read on ${source}`, 'about 15 s per 5 AVCs', checkState(ctx)),
    stepOf('fix', `Fix ${source}: build ${ctx.b}, repolicy ${ctx.p}, remove ${ctx.d}`,
      `Tool: Usher jobs on ${source} (AVC and CVC cron, every 5 min)`, 'up to 5 min', fixState(ctx)),
    stepOf('bringUp', `Bring ${source} up (${shutNow || ctx.copiesInScope.length})`, cronSystem(source), 'up to 5 min', bringUpState(ctx)),
    stepOf('restore', `Old IPs back into RADIUS (${total})${retry > 0 ? ` (${retry} can be retried)` : ''}`,
      'Tool: RADIUS and VISP pool', `about ${seconds} s`, restoreState(ctx)),
    optusStep(ctx),
    stepOf('confirmBack', 'Confirm the re-point back', 'Tool: Usher data', 'instant',
      doneBy(ctx, record.repointBackConfirmedAt, record.repointBackConfirmedBy)),
  ];
  if (ctx.activated) {
    steps.push(stepOf('moveBack', 'Move back in Usher', 'Tool: Usher data', 'instant', doneBy(ctx, record.movedBackAt, record.movedBackBy)));
  }
  if (ctx.candidates.length > 0) {
    steps.push(stepOf('kick', `Kick restored statics (${ctx.candidates.length})`, 'Tool: RADIUS disconnect (Usher)', 'about 2 s each', 'To do'));
  }
  steps.push(stepOf('close', 'Close as rolled back', `Tool: Usher data and Usher job on ${ctx.target}`, 'up to 5 min', 'To do'));
  return steps;
}

// The first step that is not done (the step that is failed or running counts as not done).
const nextStep = (ctx) => afterSteps(ctx).find(s => OPEN_STATUSES.includes(s.status));

// ---- texts ----

// Where this migration is / What rolling back means now / What customers experience.
export function afterTexts (point, ctx) {
  const record = ctx.record;
  if (point === 'rollingBack') {
    const next = nextStep(ctx);
    return {
      where: `Rolling back to ${ctx.source} since ${ctx.fmt(record.rollbackStartedAt)}${record.rollbackStartedBy ? ` (${record.rollbackStartedBy})` : ''}: `
        + `${record.rollbackReason}. Optus window: ${ctx.rbWindow || 'not booked yet'}.`,
      means: next ? `Next: ${next.label}.` : 'Nothing left to do: close the rollback.',
      customers: record.repointBackConfirmedAt ? `Customers are on ${ctx.source}.` : CUSTOMERS_UNTIL_REPOINT_BACK(ctx),
    };
  }
  if (point === 'closedRolledBack') {
    const left = ctx.t > 0 || ctx.r > 0
      ? ` Left: ${ctx.t} target copies on ${ctx.target}, ${ctx.r} new IPs reserved.`
      : '';
    return {
      where: `Closed as rolled back ${ctx.fmt(record.rolledBackAt)}${by(record.rolledBackBy)}. Customers are on ${ctx.source}.`,
      means: `Nothing to roll back.${left}`,
      customers: `Customers are on ${ctx.source}.`,
    };
  }
  return rollbackTexts(point, ctx);
}

const NO_LEDGER_POINTS = ['confirmed', 'activated', 'migrated', 'rollingBack'];

const noLedgerLine = (n) => `${n} ${plural(n, 'static has', 'statics have')} no record of ${plural(n, 'its', 'their')} old IP `
  + '(committed before the statics ledger, 22/10/2026): '
  + `the tool cannot put ${plural(n, 'it', 'them')} back. Fix ${plural(n, 'it', 'them')} in VISP after the re-point back.`;

// What cannot be undone: Release A's lines, plus the statics the tool has no old IP for.
export function afterNotPossible (point, ctx) {
  const lines = rollbackNotPossible(point, ctx);
  if (ctx.noLedger > 0 && NO_LEDGER_POINTS.includes(point)) lines.push(noLedgerLine(ctx.noLedger));
  return lines;
}

// Tasks for people outside the tool, each with the text to copy.
export function afterOutside (ctx) {
  if (ctx.point !== 'rollingBack') return [];
  const when = ctx.rbWindow;
  const items = [{
    key: 'repointBack',
    title: 'Optus re-point back',
    text: `${ctx.cvc} (${ctx.cvcId}): please re-point back to ${ctx.source}${when ? `, window ${when}` : ''}.`,
  }];
  const changed = ctx.ledger.filter(s => s.committedAt && s.oldIp && s.newIp);
  if (changed.length > 0) {
    items.push({
      key: 'wholesalersBack',
      title: 'Wholesalers before a re-point back',
      text: `${ctx.cvc} (${ctx.cvcId}): moving back to ${ctx.source}${when ? ` on ${when}` : ''}. `
        + `Static IPs return to their previous address: ${changed.map(s => `${s.username} ${s.newIp} -> ${s.oldIp}`).join(', ')}.`,
    });
  }
  ((ctx.verify && ctx.verify.missing) || []).forEach(m => {
    items.push({
      key: `network-${m.kind.toLowerCase()}`,
      title: `${ctx.cvc} ${m.kind} subinterface ${m.name} is missing on ${ctx.source}: ask network to rebuild it before Optus re-points back`,
      text: `${ctx.cvc} (${ctx.cvcId}): CVC or loop subinterface ${m.name} missing on ${ctx.source}. `
        + `Please rebuild before ${when || 'Optus re-points back'}.`,
    });
  });
  return items;
}

// Red and amber lines of a rollback in progress.
export function afterAlerts (ctx) {
  if (ctx.point !== 'rollingBack') return [];
  const alerts = [];
  if (ctx.c > 0 && ctx.evidenceRows.some(r => r.verdict === 'onSourceWithNewIp')) {
    alerts.push({
      key: 'sessionsBeforeRestore',
      type: 'error',
      text: `Sessions are arriving on ${ctx.source} before the old IPs were restored: restore now, then Kick restored statics.`,
    });
  }
  const bringUp = afterSteps(ctx).find(s => s.key === 'bringUp');
  if (bringUp && OPEN_STATUSES.includes(bringUp.status) && ctx.droppedFromTarget > 0) {
    alerts.push({
      key: 'sourceShut',
      type: 'error',
      text: `Optus re-pointed back but ${ctx.source} is still shut: bring ${ctx.source} up now.`,
    });
  }
  if (ctx.rbWindowEnded) alerts.push({ key: 'windowEnded', type: 'warning', text: 'Window ended without the re-point back' });
  return alerts;
}

// ---- buttons ----

const START_POINTS = ['confirmed', 'activated', 'migrated'];

// The buttons for the point, each on its server flag. `failed` holds the keys of the
// actions whose last run in this session failed: their button reads as a retry.
export function afterActions (steps, ctx, failed = []) {
  if (START_POINTS.includes(ctx.point)) {
    return [action(steps, 'startRollback', `Start rollback to ${ctx.source}...`, 'canStartRollback', { kind: 'start', color: 'error' })];
  }
  // A closed record keeps the leftovers of a cancelled one: the target copies and the new IPs.
  if (ctx.point === 'closedRolledBack') return rollbackActions('cancelled', steps, ctx);
  if (ctx.point !== 'rollingBack') return rollbackActions(ctx.point, steps, ctx);
  const status = (key) => (afterSteps(ctx).find(s => s.key === key) || {}).status;
  const retry = (key) => failed.includes(key);
  const list = [action(steps, 'verifySource', `Verify ${ctx.source}`, 'canVerifySource', { kind: 'verifySource', outlined: true })];
  if (ctx.b > 0) list.push(action(steps, 'buildSource', `Build on ${ctx.source} (${ctx.b})`, 'canFixSource', { kind: 'buildSource' }));
  if (ctx.p > 0) list.push(action(steps, 'repolicySource', `Repolicy on ${ctx.source} (${ctx.p})`, 'canFixSource', { kind: 'repolicySource' }));
  if (ctx.d > 0) {
    list.push(action(steps, 'removeSource', retry('removeSource') ? 'Retry removal' : `Remove from ${ctx.source} (${ctx.d})`, 'canFixSource', { kind: 'removeSource' }));
  }
  list.push(action(steps, 'bringUp', status('bringUp') === 'failed' ? `Retry bringing ${ctx.source} up` : `Bring ${ctx.source} up...`,
    'canRollback', { kind: 'bringUp', color: 'error' }));
  if (ctx.c > 0) {
    list.push(action(steps, 'restore', status('restore') === 'failed' ? `Retry restore (${ctx.c})` : `Restore old IPs (${ctx.c})...`,
      'canRestore', { kind: 'restore', color: 'error' }));
  }
  list.push(action(steps, 'window', 'Optus window...', 'canEditRollbackWindow', { kind: 'window', outlined: true }));
  list.push(action(steps, 'confirmBack', 'Confirm re-point back...', 'canConfirmRepointBack', { kind: 'confirmBack', color: 'error' }));
  if (ctx.activated) {
    list.push(action(steps, 'moveBack', retry('moveBack') ? 'Retry move back' : 'Move back in Usher...', 'canMoveBack', { kind: 'moveBack', color: 'error' }));
  }
  if (ctx.candidates.length > 0) {
    list.push(action(steps, 'kickRestored', `Kick restored statics (${ctx.candidates.length})...`, 'canKickRestored', { kind: 'kickRestored', color: 'error' }));
  }
  list.push(action(steps, 'close', 'Close as rolled back...', 'canCloseRolledBack', { kind: 'close', color: 'error' }));
  list.push(action(steps, 'stop', `Stop the rollback (stay on ${ctx.target})...`, 'canStopRollback', { kind: 'stop', color: 'error', outlined: true }));
  return list;
}

const listOf = (items) => items.join(', ');

// The plain confirm of a fix button: the AVCs or interfaces it will touch.
export function afterConfirm (kind, ctx) {
  if (kind === 'buildSource') {
    const ids = ctx.buildIds;
    return { text: `Build ${ids.length} ${plural(ids.length, 'AVC', 'AVCs')} on ${ctx.source} (AVC cron): ${listOf(ids)}.`, proceedText: 'Build', avcIds: ids };
  }
  if (kind === 'repolicySource') {
    const ids = ctx.policyIds;
    return { text: `Repolicy ${ids.length} ${plural(ids.length, 'AVC', 'AVCs')} on ${ctx.source} (AVC cron): ${listOf(ids)}.`, proceedText: 'Repolicy', avcIds: ids };
  }
  const copies = ctx.driftCopies;
  const n = copies.length;
  return {
    text: `Remove ${n} source ${plural(n, 'copy', 'copies')} of ${plural(n, 'an AVC that left', 'AVCs that left')} the CVC or ${plural(n, 'is', 'are')} `
      + `suspended from ${ctx.source} (CVC cron): ${listOf(copies.map(c => `${c.interfaceName} (${c.bridgeDomain})`))}.`,
    proceedText: 'Remove',
    copyIds: copies.map(c => c.id),
  };
}

// ---- dialogs ----

const ipChange = (s) => `${s.username} ${s.newIp} -> ${s.oldIp}`;
const missingLine = (ctx, m) => `${ctx.cvc} ${m.kind} subinterface ${m.name} is missing on ${ctx.source}: ask network to rebuild it before Optus re-points back`;
const missingAlerts = (ctx) => ((ctx.verify && ctx.verify.missing) || []).map(m => ({ type: 'warning', text: missingLine(ctx, m) }));
const count = (n, one, many) => `${n} ${plural(n, one, many)}`;

function outsideWindow (ctx) {
  const start = toDate(ctx.record.rollbackWindowStart);
  const end = toDate(ctx.record.rollbackWindowEnd);
  const now = toDate(ctx.nowUtc);
  return !!start && !!end && !!now && (now < start || now > end);
}

// The AVCs the move back swaps: the VISP AVCs on the LOCAL customer. When no row names
// that customer, every VISP AVC in service is listed (as the Activate dialog counts them).
function localAvcIds (ctx) {
  const visp = ctx.allRows.filter(r => r.avcClass === 'VISP' && r.serviceStatus !== 'inactive-delete');
  const local = visp.filter(r => ctx.localName && r.customerName === ctx.localName);
  return (local.length > 0 ? local : visp).map(r => r.avcId);
}

// AVCs recorded on the source after activation with no copy on the target: built by
// Usher's order flow, so the tool cannot remove them from the target.
function orderedAfterActivation (ctx) {
  const activated = timeMs(ctx.record.activatedAt);
  const onTarget = unremoved(ctx.record.targetCopies).map(c => c.avcId);
  return unremoved(ctx.record.sourceCopies)
    .filter(c => timeMs(c.recordedAt) > activated && !onTarget.includes(c.avcId))
    .map(c => c.avcId);
}

const startBody = (ctx) => `Optus re-pointed ${ctx.cvc} to ${ctx.target}. Going back needs Optus to re-point it back to ${ctx.source}. `
  + `The page then guides you: check and bring up ${ctx.source}, restore the old IPs of ${count(ctx.c, 'static', 'statics')} at the start of the Optus window, `
  + `confirm the re-point back${ctx.activated ? `, move Usher back to ${ctx.source}` : ''}, close the record. `
  + 'Pressing Start changes nothing on a router, in RADIUS or on the AVCs. Forward steps close until you finish or stop the rollback.';

const WINDOW_FIELD = { label: 'Optus re-point-back window', notBookedLabel: 'Not booked yet' };

// Every dialog of the rollback. Start, the Optus window and confirm back need
// several fields, so they are `form` dialogs (fields, ticks, window); the others fit the
// page's step dialog (a tick or the typed CVC name).
export function afterDialog (kind, ctx) {
  const cvc = ctx.cvc;
  const source = ctx.source;
  const target = ctx.target;
  if (kind === 'start') {
    return {
      form: true,
      kind,
      title: `Start rolling back ${cvc} to ${source}`,
      body: [startBody(ctx)],
      alerts: ctx.noLedger > 0 ? [{ type: 'error', text: noLedgerLine(ctx.noLedger) }] : [],
      fields: [
        { key: 'reason', label: 'Reason', maxlength: 200, required: true },
        { key: 'agreedBy', label: 'Agreed by', hint: 'e.g. Name - network lead', maxlength: 50, required: true },
      ],
      window: WINDOW_FIELD,
      ticks: [{ key: 'asked', label: `I have asked Optus to re-point ${cvc} back to ${source}`, required: true }],
      requireTyping: true,
      proceedLabel: 'Start rollback',
    };
  }
  if (kind === 'window') {
    return {
      form: true,
      kind,
      title: 'Optus re-point-back window',
      body: ['Change the window Optus is booked to re-point back in. Nothing changes on a router, in RADIUS or on the AVCs.'],
      alerts: [],
      fields: [],
      window: WINDOW_FIELD,
      ticks: [],
      requireTyping: false,
      proceedLabel: 'Save window',
    };
  }
  if (kind === 'bringUp') {
    const n = ctx.copiesShut.length;
    return {
      title: `Bring ${source} up for ${cvc}`,
      body: [`Un-shuts ${count(n, 'subinterface', 'subinterfaces')} of ${cvc} on ${source} (CVC cron). `
        + `Optus still points ${cvc} at ${target}, so ${source} carries no traffic until Optus re-points back.`],
      checkbox: { label: `Optus has not re-pointed ${cvc} back yet, or I am ready for it`, shown: true, required: true },
      proceedLabel: `Bring ${source} up`,
      requireTyping: false,
    };
  }
  if (kind === 'restore') {
    const alerts = [];
    if (ctx.rbWindow && outsideWindow(ctx)) alerts.push({ type: 'warning', text: 'You are outside the booked re-point-back window.' });
    const bringUp = afterSteps(ctx).find(s => s.key === 'bringUp');
    if (bringUp && OPEN_STATUSES.includes(bringUp.status)) {
      alerts.push({ type: 'warning', text: `${source} is not up yet: bring it up before Optus re-points back.` });
    }
    return {
      title: `Put back the old IPs of ${count(ctx.c, 'static', 'statics')}`,
      body: [`Writes the old IP back into RADIUS for ${count(ctx.c, 'static', 'statics')} (user new -> old below). Each static gets its old IP when it next connects. `
        + `Until Optus re-points back, a static that reconnects on ${target} gets its old IP there, which works with poor latency. `
        + 'Do this at the start of the Optus window.'],
      consequences: ctx.ledger.filter(s => s.state === 'committed').map(ipChange),
      alerts: alerts.concat(missingAlerts(ctx)),
      proceedLabel: 'Restore old IPs',
      requireTyping: true,
    };
  }
  if (kind === 'confirmBack') {
    const since = ctx.fmt(ctx.backOnSource > 0 ? ctx.firstBackOnSource || ctx.evidenceSince : ctx.evidenceSince || ctx.record.rollbackStartedAt);
    const body = [`Back on ${source} since ${since}: ${ctx.backOnSource} of ${ctx.evidenceMonitored} services.`];
    const alerts = [];
    if (ctx.backOnSource === 0) {
      alerts.push({
        type: 'warning',
        text: `No session has reached ${source} since the restore. Confirm only if a network engineer has checked ${cvc} on ${source}.`,
      });
    }
    const ticks = [{ key: 'confirmed', label: `Optus has confirmed the re-point back to ${source}`, required: true }];
    if (ctx.b > 0) {
      body.push(`Built after activation, no copy on ${source}: ${listOf(ctx.buildIds)}`);
      ticks.push({ key: 'builtByNetwork', label: `Network built ${listOf(ctx.buildIds)} on ${source}`, required: true });
    }
    return {
      form: true,
      kind,
      title: `Confirm Optus re-pointed ${cvc} back to ${source}`,
      body,
      alerts: alerts.concat(missingAlerts(ctx)),
      fields: [{ key: 'confirmedBy', label: 'Confirmed by', hint: `e.g. Name - CVC up on ${source}`, maxlength: 50, required: true }],
      window: null,
      ticks,
      requireTyping: false,
      proceedLabel: 'Confirm re-point back',
    };
  }
  if (kind === 'moveBack') {
    const ids = localAvcIds(ctx);
    const domains = [...new Set(unremoved(ctx.record.sourceCopies).map(c => c.bridgeDomain).filter(Boolean))];
    return {
      title: `Move ${cvc} back to ${source} in Usher`,
      body: [`CVC pointer -> ${source}, ${ids.length} VISP AVCs -> their central customer (${domains.length ? domains.join(', ') : 'central bridge domain'}), `
        + `new orders on this CVC go to ${source} again, new static IPs for these services come from the ${ctx.state} pool again. No router change.`],
      consequences: ids,
      proceedLabel: 'Move back',
      requireTyping: false,
    };
  }
  if (kind === 'kickRestored') {
    const k = ctx.candidates.length;
    const rows = ctx.evidenceRows.filter(r => ctx.candidates.includes(r.radiusServiceId));
    return {
      title: `Kick ${k} restored ${plural(k, 'static', 'statics')}`,
      body: [`Drop the current session of ${count(k, 'static', 'statics')} that ${plural(k, 'is', 'are')} online with ${plural(k, 'its', 'their')} new IP, `
        + `so ${plural(k, 'it', 'they')} reconnect with ${plural(k, 'its', 'their')} old IP.`],
      consequences: rows.map(r => `${r.username} on ${r.lastNasIp} with ${r.lastAcctIp} since ${ctx.fmt(r.lastSessionStart)}`),
      proceedLabel: 'Kick',
      requireTyping: false,
    };
  }
  if (kind === 'close') {
    const alerts = [];
    if (ctx.c > 0) {
      alerts.push({
        type: 'warning',
        text: `${count(ctx.c, 'static', 'statics')} still ${plural(ctx.c, 'carries its', 'carry their')} new IP. `
          + 'Closing lets the quarantine sweep free their old IPs at the next 08:00 or 15:00 run: fix them in VISP first.',
      });
    }
    const ordered = orderedAfterActivation(ctx);
    if (ordered.length > 0) {
      alerts.push({
        type: 'warning',
        text: `Not removed by the tool: ${listOf(ordered)} ${plural(ordered.length, 'was', 'were')} ordered after activation and built on ${target}. `
          + `Ask network to remove ${plural(ordered.length, 'it', 'them')} from ${target}.`,
      });
    }
    const targetCopyIds = unremoved(ctx.record.targetCopies).map(c => c.id);
    return {
      title: `Close the migration of ${cvc} as rolled back`,
      body: [`Steps: 1 close the record as rolled back, 2 remove ${targetCopyIds.length} target copies from ${target}, `
        + `3 release ${ctx.r} new IPs (leave them reserved if this CVC moves again soon; `
        + `released ${ctx.state} IPs can be taken by other ${ctx.state} static requests).`],
      alerts,
      checkbox: { label: `Release ${ctx.r} new IPs`, shown: ctx.r > 0, checked: true },
      proceedLabel: 'Close as rolled back',
      requireTyping: true,
      targetCopyIds,
      reserved: ctx.r,
    };
  }
  const sourceUp = ctx.copiesInScope.some(c => !c.shutAt);
  const n = ctx.restoredSinceStart;
  const commit = n > 0
    ? ` Then Commit statics (section C) to give the ${count(n, 'restored static', 'restored statics')} ${plural(n, 'its', 'their')} new IP again.`
    : '';
  return {
    title: `Stop rolling back ${cvc}: stay on ${target}`,
    body: [`${sourceUp ? `${source} is shut again (CVC cron). ` : ''}Tell Optus not to re-point back.${commit}`],
    proceedLabel: `Stay on ${target}`,
    requireTyping: true,
  };
}

// ---- forms of the form dialogs ----

// An empty form for the dialog. The window form holds a Sydney date and time (as
// section A does); the Optus window dialog starts from the window on the record.
export function emptyForm (dialog, ctx) {
  const saved = dialog.kind === 'window' ? windowToForm(ctx.record.rollbackWindowStart, ctx.record.rollbackWindowEnd) : null;
  return {
    values: Object.fromEntries(dialog.fields.map(f => [f.key, ''])),
    ticks: Object.fromEntries(dialog.ticks.map(t => [t.key, false])),
    notBooked: dialog.kind === 'window' && !saved,
    window: saved || { date: '', start: '10:00', duration: DEFAULT_WINDOW_DURATION },
    typed: '',
  };
}

// The proceed button: the typed name, every required field within its length, every
// required tick, and a window (or the not booked tick) where the dialog asks for one.
export function formReady (dialog, form, cvcName) {
  if (dialog.requireTyping && !requiresTypedName(form.typed, cvcName)) return false;
  const fieldsOk = dialog.fields.every(f => {
    const value = (form.values[f.key] || '').trim();
    return (!f.required || value.length > 0) && (!f.maxlength || value.length <= f.maxlength);
  });
  if (!fieldsOk) return false;
  if (!dialog.ticks.every(t => !t.required || form.ticks[t.key] === true)) return false;
  return !dialog.window || form.notBooked || !!windowFromForm(form.window);
}

export function windowRequest (form) {
  const w = form.notBooked ? null : windowFromForm(form.window);
  return w
    ? { windowStartUtc: w.startUtc, windowEndUtc: w.endUtc, windowNotBooked: false }
    : { windowStartUtc: null, windowEndUtc: null, windowNotBooked: true };
}

export function startRequest (form) {
  return { reason: form.values.reason.trim(), agreedBy: form.values.agreedBy.trim(), ...windowRequest(form) };
}

// Only the network tick says the AVCs without a copy were built by hand.
export function confirmBackRequest (form) {
  return { confirmedBy: form.values.confirmedBy.trim(), builtByNetwork: form.ticks.builtByNetwork === true };
}

// ---- results ----

const BATCH_TONE = { failed: 'red', skipped: 'amber', done: 'green' };

// What the panel shows under the buttons once a step answered: the server message, and
// one row per AVC for the steps that work on AVCs (failures first).
export function afterResult (kind, answer, ctx) {
  const reply = answer || {};
  if (kind === 'bringUp' || kind === 'restore') {
    const view = rollbackAnswerView(reply);
    return { type: view.type, text: view.text, rows: view.rows };
  }
  if (kind === 'buildSource' || kind === 'repolicySource') {
    const summary = batchSummary(`${kind === 'buildSource' ? 'Build' : 'Repolicy'} on ${ctx.source}`, reply, ctx.nowUtc, ctx.poiState);
    return { type: summary.type, text: summary.text, rows: summary.rows.map(r => ({ ...r, tone: BATCH_TONE[r.outcome] || 'grey' })) };
  }
  const moved = (reply.moved || []).map(avcId => ({ avcId, outcome: 'moved', message: '', tone: 'green' }));
  return { type: 'success', text: reply.message || 'Done', rows: moved };
}

// The kick run the rollback panel shows: only a run of restored statics (a Kick all is
// section D's, and section D is closed while a rollback runs).
export const restoredKickRun = (run) => (run && run.kind === 'restored' ? run : null);
